import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { SUPPORTED_LANGUAGES, translations } from '../i18n/translations';

const STORAGE_KEY = 'poupix_lang';
const DEFAULT_LANGUAGE = 'pt';

const I18nContext = createContext(null);

const detectBrowserLanguage = () => {
  if (typeof navigator === 'undefined') {
    return null;
  }

  const candidates = [];
  if (Array.isArray(navigator.languages)) {
    candidates.push(...navigator.languages);
  }
  if (navigator.language) {
    candidates.push(navigator.language);
  }

  for (const raw of candidates) {
    const normalized = (raw || '').toLowerCase();
    const base = normalized.split('-')[0];
    if (SUPPORTED_LANGUAGES.includes(base)) {
      return base;
    }
  }

  return null;
};

const getStoredLanguage = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED_LANGUAGES.includes(saved)) {
      return saved;
    }
  } catch (_) {
    // Ignore localStorage access errors.
  }

  const detected = detectBrowserLanguage();
  if (detected) {
    return detected;
  }

  return DEFAULT_LANGUAGE;
};

const getByPath = (obj, path) => {
  return path.split('.').reduce((acc, key) => (acc && key in acc ? acc[key] : undefined), obj);
};

const interpolate = (template, params = {}) => {
  if (typeof template !== 'string') return template;
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => {
    const value = params[key];
    return value === undefined || value === null ? '' : String(value);
  });
};

export const I18nProvider = ({ children }) => {
  const [language, setLanguageState] = useState(getStoredLanguage);

  const setLanguage = useCallback((nextLang) => {
    if (!SUPPORTED_LANGUAGES.includes(nextLang)) return;
    setLanguageState(nextLang);
    try {
      localStorage.setItem(STORAGE_KEY, nextLang);
    } catch (_) {
      // Ignore localStorage write errors.
    }
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
    }
  }, [language]);

  const t = useCallback((key, params) => {
    const current = getByPath(translations[language] || {}, key);
    const fallback = getByPath(translations[DEFAULT_LANGUAGE] || {}, key);
    const resolved = current ?? fallback ?? key;
    return interpolate(resolved, params);
  }, [language]);

  const value = useMemo(
    () => ({ language, setLanguage, t, supportedLanguages: SUPPORTED_LANGUAGES }),
    [language, setLanguage, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
};
